import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function Privacy() {
  const { user } = useAuth();
  const back = user ? "/home" : "/";
  return (
    <div data-testid="privacy-container" className="min-h-screen bg-[#FDFBF7]">
      <div className="max-w-3xl mx-auto px-6 lg:px-10 py-14">
        <Link to={back} className="inline-flex items-center gap-1.5 text-sm text-[#2C2520]/70 hover:text-[#2C2520]">
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Back
        </Link>
        <div className="label-caps mt-8 mb-4">Privacy policy</div>
        <h1 className="font-serif-display text-4xl sm:text-5xl text-[#2C2520] tracking-tight">
          Your reading life, kept private.
        </h1>
        <p className="mt-3 text-sm text-[#2C2520]/60">Last updated: {new Date().toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })}</p>

        <div className="mt-10 space-y-8 text-[#2C2520]/85 leading-relaxed">
          <Section title="A short summary, in plain words">
            <p>
              Bibliotherapy Companion is designed to feel like a quiet reading nook. What you write here is yours.
              We collect the smallest amount of information needed to make your recommendations personal, and we
              never sell your data.
            </p>
          </Section>

          <Section title="What we collect">
            <ul className="list-disc pl-5 space-y-1.5">
              <li><strong>Account</strong>: your name, email, and password (stored securely as a bcrypt hash).</li>
              <li><strong>Mood check-ins</strong>: the emotions and intensity you choose, and any optional note.</li>
              <li><strong>Journal entries</strong>: the free-text reflections you write or speak.</li>
              <li><strong>Saved library and reading list</strong>: the books, articles, podcasts, videos, and practices you save, along with their status.</li>
              <li><strong>Reflections</strong>: reviews you post to the community feed (visible to other users; you can delete them at any time).</li>
              <li><strong>Basic technical data</strong>: standard server logs and cookies used to keep you signed in.</li>
            </ul>
          </Section>

          <Section title="What we don't do">
            <ul className="list-disc pl-5 space-y-1.5">
              <li>We do not sell, rent, or share your journal entries or mood data with anyone.</li>
              <li>We do not advertise inside the app.</li>
              <li>We do not use your personal writing to train third-party AI models.</li>
            </ul>
          </Section>

          <Section title="How we use AI">
            <p>
              Your mood entries and journal reflections are sent to a large language model (Anthropic's Claude,
              accessed through Emergent) to generate personalized recommendations. Only the content necessary for
              that one recommendation is sent, and the model provider does not retain the content for training.
            </p>
          </Section>

          <Section title="Cookies">
            <p>
              We use a single secure, http-only cookie to keep you signed in. There are no advertising or
              analytics trackers embedded in the app itself.
            </p>
          </Section>

          <Section title="Data you can control">
            <p>
              You can delete any journal entry, mood check-in, saved item, reading list book, or community
              reflection you've created. If you would like your entire account deleted, email us and we will remove
              it within 30 days.
            </p>
          </Section>

          <Section title="Affiliate links">
            <p>
              We include affiliate links to booksellers such as Amazon and Bookshop.org. When you buy through those
              links, Bibliotherapy Companion may earn a small commission at no extra cost to you. This helps keep the
              app free. See our <Link to="/disclosure" className="text-[#4A5D4E] underline underline-offset-4">Affiliate Disclosure</Link>.
            </p>
          </Section>

          <Section title="Children">
            <p>
              Bibliotherapy Companion is intended for readers 13 and older. We do not knowingly collect information
              from children under 13.
            </p>
          </Section>

          <Section title="Contact">
            <p>
              Questions or requests? Please reach out via the support email listed in the app store. We read every
              message.
            </p>
          </Section>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="font-serif-display text-xl text-[#2C2520] mb-3">{title}</h2>
      <div className="space-y-3">{children}</div>
    </section>
  );
}
